<template>
	<CardGrafico
		:title="configuracaoDoGrafico.titulo"
		:subtitle="dataDoUltimoProcessamento"
	>
		<template #botoes>
			<ButtonDropdown
				v-model="dropdownTipoGrafico"
				:label="$t('plantedArea')"
				color="trv-default"
				semantic-class="mini"
				:options="[
					{ label: $t('plantedArea'), value: 'areaPlantada' },
					{ label: $t('totalVolumeWithBark'), value: 'volume' },
					{
						label: $t('aai'),
						value: 'incrementoMedioAnual',
					},
				]"
				outlined
			/>
			<!-- <ButtonDropdown
        style="margin-top: 0.5rem"
        color="trv-neutral"
        :label="$t('download')"
        semanticClass="mini"
        :options="[
          { label: 'PNG', value: 'image/png' },
          { label: 'JPEG', value: 'image/jpeg' },
          { label: 'PDF', value: 'application/pdf' },
          { label: 'SVG', value: 'image/svg+xml' },
          { label: '.csv', value: 'text/csv' },
        ]"
        v-model="dropdownDownload"
        outlined
      /> -->
		</template>
		<template #grafico>
			<Loading v-if="data.length === 0" />
			<div
				v-else-if="!possuiValoresParaVariavelSelecionada"
				class="container__graficofazenda__semdados"
			>
				{{ $t('semDadosParaVariavelNoGraficoMaterialGenetico', { variavel: configuracaoDoGrafico.nomeDaVariavel }) }}
			</div>
			<div
				v-else
				class="container__graficofazenda"
			>
				<Chart
					ref="ref__chart"
					:options="chartOptions"
				/>
			</div>
		</template>
	</CardGrafico>
</template>

<script>
import { Chart } from 'highcharts-vue'
import ButtonDropdown from '../../../../components/ButtonDropdown/ButtonDropdown'
import CardGrafico from '../CardGrafico'
import Loading from '../../../../components/Loading/Loading'

import _ from 'lodash'

import { stringToExportCSV } from '../../../../utils/csvHellpers'

import i18n from '../../../../VueI18n'

/**
 * Formata um valor numerico do tooltip, exibindo "-" quando o valor nao existe
 * (ex.: estrato sem volume ou IMA processado)
 */
const formatarValorTooltip = (valor) => (typeof valor === 'number' ? valor.toFixed(2) : '-')

export default {
	components: { ButtonDropdown, CardGrafico, Chart, Loading },
	props: {
		/**
		 * Dados que devem ir para o grafico
		 */
		data: { type: Array, required: true },

		/**
		 * Data do ultimo processamento
		 */
		dataDoUltimoProcessamento: { type: String, require: true },
	},
	data() {
		return {
			dropdownTipoGrafico: 'areaPlantada',
			dropdownDownload: '',
			chartOptions: {
				/**
				 * Creditos no canto inferior direito do grafico
				 */
				credits: {
					enabled: false,
				},
				exporting: {
					enabled: false,
				},
				/**
				 * Dados que devem ser colocados no grafico
				 */
				series: [
					{
						type: 'column',
						name: i18n.t('plantedArea'),
						data: [],
						dataLabels: {
							align: 'right',
							crop: false,
							enabled: true,
							style: {
								fontFamily: 'Open Sans, sans-serif',
								fontSize: '.75rem',
								fontWeight: 400,
							},
							rotation: -90,
							y: -45,
						},
					},
				],
				/**
				 * Titulo do grafico
				 */
				title: {
					text: '',
				},
				/**
				 * Balão ao passar o mouse sobre as barras
				 */
				tooltip: {
					formatter: function () {
						return `
            <div class="container__tooltip"> 
              <div class="container__tooltip__title"> ${this.key} </div>
              <div class="container__tooltip__info"> 
                <div class="container__tooltip__info__individual">
                  <div>${i18n.t('plantedArea')} : </div> 
                  <div>${formatarValorTooltip(this.point.options.tooltipData.areaPlantada)} (ha) </div> 
                </div>
                <div class="container__tooltip__info__individual">
                  <div>${i18n.t('totalVolumeWithBark')} : </div> 
                  <div>${formatarValorTooltip(this.point.options.tooltipData.volume)} (m³/ha)</div> 
                </div>
                <div class="container__tooltip__info__individual">
                  <div>${i18n.t('ima')} : </div> 
                  <div>${formatarValorTooltip(this.point.options.tooltipData.incrementoMedioAnual)} ${i18n.t('ano')}</div>
                </div>
              </div>
            </div>
            `
					},
					backgroundColor: '#333647',
					borderRadius: 10,
					borderWidth: 0,
					outside: true,
					shadow: false,
					useHTML: true,
				},
				/**
				 * Legendas do gráfico
				 */
				legend: {
					enabled: false,
				},
				/**
				 * Eixo X
				 */
				xAxis: {
					type: 'category',
					categories: [],
					tickInterval: 1,
					visible: true,
					labels: {
						rotation: 270,
						style: {
							color: '#01395e',
							fontFamily: 'Open Sans, sans-serif',
							fontSize: '.8rem',
							fontWeight: 700,
						},
					},
				},
				/**
				 * Eixo Y
				 */
				yAxis: {
					gridLineColor: '#FFFFFF',
					title: {
						text: i18n.t('areaPlantada'),
						style: {
							fontFamily: 'Open Sans, sans-serif',
							fontSize: '.8rem',
							fontWeight: 600,
						},
					},
					labels: {
						enabled: false,
					},
				},
			},
		}
	},
	computed: {
		/**
		 * Configuracao do grafico de acordo com o tipo exibido
		 */
		configuracaoDoGrafico() {
			if (this.dropdownTipoGrafico === 'areaPlantada') {
				return {
					tipoDoGrafico: `${this.$t('plantedArea')} (ha)`,
					nomeDaVariavel: this.$t('plantedArea'),
					titulo: `${this.$t('plantedAreaByGeneticMaterial')}`,
				}
			} else if (this.dropdownTipoGrafico === 'volume') {
				return {
					tipoDoGrafico: `${this.$t('totalVolumeWithBark')} (m³/ha)`,
					nomeDaVariavel: this.$t('totalVolumeWithBark'),
					titulo: `${this.$t('totalVolumeByGeneticMaterial')}`,
				}
			} else {
				return {
					tipoDoGrafico: `${this.$t('aai')} ${this.$t('ano')}`,
					nomeDaVariavel: this.$t('aai'),
					titulo: `${this.$t('aaiByGeneticMaterial')}`,
				}
			}
		},
		/**
		 * Indica se ao menos um material genetico possui valor para a variavel do dropdown
		 * (ex.: estrato sem IMA processado retorna todos os valores nulos)
		 */
		possuiValoresParaVariavelSelecionada() {
			return this.data.some(
				(dadosPorMaterialGenetico) => typeof dadosPorMaterialGenetico[this.dropdownTipoGrafico] === 'number',
			)
		},
	},
	watch: {
		/**
		 * Sempre que o data mudar ira renderizar o grafico novo
		 */
		data() {
			this.changeHighchartsOptions()
		},
		/**
		 * Tipo do grafico que deve ser mostrado
		 * Área Plantada, Volume ou IMA
		 */
		dropdownTipoGrafico() {
			this.changeHighchartsOptions()
		},
		/**
		 * Seleciona a opcao de download escolhida
		 */
		dropdownDownload(type) {
			if (type) {
				const filename = `${new Date().getTime()}`
				if (type === 'text/csv') {
					const csv = this.$refs.ref__chart.chart.getCSV()
					stringToExportCSV(filename, csv)
				} else {
					this.$refs.ref__chart.chart.exportChart({ filename, type })
				}
				this.dropdownDownload = ''
			}
		},
	},
	created() {
		const rgbDefaultColor = getComputedStyle(document.body).getPropertyValue('--color-default')
		this.chartOptions.series[0].color = `rgb(${rgbDefaultColor})`
	},
	methods: {
		/**
		 * Seta os dados do grafico de acordo com o valor que estiver no dropdown
		 */
		changeHighchartsOptions() {
			const sortedData = _.orderBy(this.data, this.dropdownTipoGrafico, ['desc'])

			let opacity = 1
			const opacityDecrease = 0.7 / sortedData.length

			const data = sortedData.map((dadosPorMaterialGenetico) => {
				const valor = dadosPorMaterialGenetico[this.dropdownTipoGrafico]
				const result = {
					// Valores nulos (estrato sem o indicador processado) ficam sem barra em vez de quebrar o watcher
					y: typeof valor === 'number' ? parseFloat(valor.toFixed(2)) : null,
					opacity,
					tooltipData: dadosPorMaterialGenetico,
				}
				opacity -= opacityDecrease
				return result
			})

			const categories = sortedData.map((value) => {
				return value.materialGenetico
			})

			this.chartOptions.series[0].data = data
			this.chartOptions.xAxis.categories = categories
			this.chartOptions.yAxis.max = Math.max(0, ...data.map((m) => m.y ?? 0)) + 100
			this.chartOptions.yAxis.title.text = this.configuracaoDoGrafico.tipoDoGrafico
		},
	},
}
</script>

<style scoped>
@import url('../../../../css/tooltipSimplesHighcharts.css');
.container__graficofazenda {
	display: grid;
}
.container__graficofazenda__semdados {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 200px;
	padding: 1rem;
	text-align: center;
	font-size: 0.875rem;
	color: var(--color-gray);
}
</style>
